import { useCallback } from "react";

import {
  suggestionOptionId,
  useSuggestionsListContext,
} from "@/components/suggestions/navigation/SuggestionsListProvider";

import type { Suggestion } from "@/components/suggestions/types";

type ItemProps = Record<string, unknown> & {
  onMouseDown?: React.MouseEventHandler;
  onMouseMove?: React.MouseEventHandler;
};

export function useListItemProps({
  suggestion,
  index,
  listId,
  isActive,
}: {
  suggestion: Suggestion;
  index: number;
  listId: string | null;
  isActive: boolean;
}) {
  const { menuId, onSelect, onActiveIndexChange } = useSuggestionsListContext();
  const id = suggestionOptionId(menuId, listId, index);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      onSelect(suggestion, undefined, true);
    },
    [onSelect, suggestion],
  );

  const handleMouseMove = useCallback(() => {
    onActiveIndexChange(listId, index);
  }, [listId, onActiveIndexChange, index]);

  return useCallback(
    (externalProps?: ItemProps) => ({
      role: "menuitem" as const,
      id,
      ...externalProps,
      tabIndex: isActive ? 0 : -1,
      onMouseDown: (e: React.MouseEvent) => {
        handleMouseDown(e);
        externalProps?.onMouseDown?.(e);
      },
      onMouseMove: (e: React.MouseEvent) => {
        handleMouseMove();
        externalProps?.onMouseMove?.(e);
      },
    }),
    [id, isActive, handleMouseDown, handleMouseMove],
  );
}
